import React, { createContext, useContext, useState, useCallback } from "react";
import { api, setAuthToken, getStoredToken } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { getSource } from "@/lib/attribution";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      setAuthToken(null);
      return null;
    }
  }, []);

  useImperativeLoad(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setAuthToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (email, password, name, referralCode) => {
    const body = { email, password, name };
    if (referralCode) body.referral_code = referralCode;
    try {
      const loc = localStorage.getItem("neuracom_locale");
      if (loc && ["it", "en", "pl"].includes(loc)) body.locale = loc;
    } catch (e) { /* ignore */ }
    const src = getSource();
    if (src) body.source = src;
    const res = await api.post("/auth/register", body);
    setAuthToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const loginWithEmergent = useCallback(async (sessionId) => {
    const res = await api.post("/auth/emergent/session", {
      session_id: sessionId,
      source: getSource() || null,
    });
    setAuthToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAuthToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, loginWithEmergent, logout, refresh, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
